import { Store, MapPin, ShoppingBag, Globe, Menu, Calendar, Handshake } from 'lucide-react';
import { motion } from 'framer-motion';

/**
 * BusinessHeader - Premium identity section with elegant design
 */

export function BusinessHeader({
    businessName,
    category,
    location,
    isOwner,
    onMenuToggle,
    onCreateStore,
    onCreateEvent,
    onGoToStore,
    hasStore,
    websiteUrl,
    toggleButtonRef,
    businessType
}) {
    // Determine icon and gradient based on business type
    const getIconAndGradient = () => {
        if (businessType === 'event') {
            return {
                icon: Calendar,
                gradient: 'from-brand-400 to-brand-500',
                shadow: 'shadow-brand-500/20'
            };
        } else if (businessType === 'barter') {
            return {
                icon: Handshake,
                gradient: 'from-success-500 to-success-500',
                shadow: 'shadow-success-500/20'
            };
        } else {
            return {
                icon: Store,
                gradient: 'from-warning-500 to-warning-500',
                shadow: 'shadow-warning-500/20'
            };
        }
    };

    const { icon: IconComponent, gradient, shadow } = getIconAndGradient();

    return (
        <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="bg-gradient-to-br from-white via-warning-50/30 to-warning-50/20 px-4 py-4 md:px-6 md:py-8 border-b border-warning-100/50"
        >
            {/* Row 1: Icon + Title */}
            <div className="flex items-center gap-2 md:gap-3 mb-3">
                <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ duration: 0.5, delay: 0.1, type: "spring" }}
                    className={`flex-shrink-0 w-8 h-8 md:w-11 md:h-11 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg ${shadow}`}
                >
                    <IconComponent className="w-4 md:w-6 h-4 md:h-6 text-white" strokeWidth={2} />
                </motion.div>
                <h1 className="text-lg md:text-2xl font-bold text-ink-strong leading-tight tracking-tight line-clamp-2">
                    {businessName}
                </h1>
            </div>

            {/* Row 2: Badges + Action Buttons */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
                {/* Category + Location Badges */}
                <div className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm text-ink-muted flex-wrap">
                    {category && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 md:px-3 md:py-1 rounded-full bg-white/80 border border-warning-100/50 font-medium text-warning-700 shadow-sm text-xs md:text-sm">
                            {category}
                        </span>
                    )}
                    {location && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 md:px-3 md:py-1 rounded-full bg-white/80 border border-line/50 font-medium text-ink shadow-sm text-xs md:text-sm">
                            <MapPin className="w-3 h-3 md:w-3.5 md:h-3.5 text-ink-subtle" strokeWidth={2} />
                            {location}
                        </span>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 md:gap-4">
                    {/* Website Button */}
                    {websiteUrl && (
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2 }}
                            className="flex flex-col items-center gap-1.5"
                        >
                            <a
                                href={websiteUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group relative flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-success-500 to-success-500 text-white shadow-lg shadow-success-500/25 hover:shadow-xl hover:shadow-success-500/30 transition-all duration-300 hover:scale-110 active:scale-95"
                                title="למעבר לאתר"
                            >
                                <Globe className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2} />
                            </a>
                            <span className="text-xs md:text-sm font-semibold text-ink text-center">אתר</span>
                        </motion.div>
                    )}

                    {/* Store Button - Not shown for barters */}
                    {hasStore && businessType !== 'barter' && (
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.25 }}
                            className="flex flex-col items-center gap-1.5"
                        >
                            <button
                                onClick={onGoToStore}
                                className="group relative flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-info-500 to-brand-500 text-white shadow-lg shadow-info-500/25 hover:shadow-xl hover:shadow-info-500/30 transition-all duration-300 hover:scale-110 active:scale-95"
                                title="עבור לחנות"
                            >
                                <ShoppingBag className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2} />
                            </button>
                            <span className="text-xs md:text-sm font-semibold text-ink text-center">חנות</span>
                        </motion.div>
                    )}

                    {/* Owner: Create Store Button - Not shown for barters */}
                    {isOwner && !hasStore && businessType !== 'barter' && (
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.25 }}
                            className="flex flex-col items-center gap-1.5"
                        >
                            <button
                                onClick={onCreateStore}
                                className="group relative flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-success-500 to-success-500 text-white shadow-lg shadow-success-500/25 hover:shadow-xl hover:shadow-success-500/30 transition-all duration-300 hover:scale-110 active:scale-95"
                                title="פתח חנות"
                            >
                                <ShoppingBag className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2} />
                            </button>
                            <span className="text-xs md:text-sm font-semibold text-ink text-center">צור חנות</span>
                        </motion.div>
                    )}

                    {/* Owner: Add Event Button - Only shown for business type */}
                    {isOwner && businessType === 'business' && onCreateEvent && (
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 }}
                            className="flex flex-col items-center gap-1.5"
                        >
                            <button
                                onClick={onCreateEvent}
                                className="group relative flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-brand-500 to-brand-500 text-white shadow-lg shadow-brand-500/25 hover:shadow-xl hover:shadow-brand-500/30 transition-all duration-300 hover:scale-110 active:scale-95"
                                title="צור מפגש"
                            >
                                <Calendar className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2} />
                            </button>
                            <span className="text-xs md:text-sm font-semibold text-ink text-center">צור מפגש</span>
                        </motion.div>
                    )}

                    {/* Owner: Menu Button */}
                    {isOwner && (
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.35 }}
                            className="flex flex-col items-center gap-1.5"
                        >
                            <button
                                ref={toggleButtonRef}
                                onClick={onMenuToggle}
                                className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-white border border-line shadow-md hover:shadow-lg hover:bg-surface-muted transition-all duration-300 hover:scale-110 active:scale-95"
                                aria-label="Menu"
                            >
                                <Menu className="w-5 h-5 md:w-6 md:h-6 text-ink" strokeWidth={2} />
                            </button>
                            <span className="text-xs md:text-sm font-semibold text-ink text-center">תפריט</span>
                        </motion.div>
                    )}
                </div>
            </div>
        </motion.div>
    );
}

export default BusinessHeader;
